import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, ChevronRight, Sparkles, Wind, Shield, Home, Phone } from "lucide-react";

const sandingServices = [
  {
    id: 1,
    name: "Floor Sanding",
    description: "Bring tired wooden floors back to life. I'll sand down to bare wood, removing years of wear, scratches and old finishes.",
    icon: Sparkles,
    features: ["Hardwood floors", "Parquet flooring", "Floorboards", "Engineered wood"]
  },
  {
    id: 2,
    name: "Dust-Free Sanding",
    description: "Using professional dust extraction units, I capture dust at the source. Your home stays virtually dust-free throughout the job.",
    icon: Wind,
    features: ["Portable extraction", "Near-zero dust", "No mess", "Clean as you go"]
  },
  {
    id: 3,
    name: "Floor Restoration",
    description: "Deep scratches, water damage, or just years of wear? I can restore wooden floors to their original beauty.",
    icon: Home,
    features: ["Scratch removal", "Stain treatment", "Gap filling", "Board repairs"]
  },
  {
    id: 4,
    name: "Finishing & Sealing",
    description: "Once sanded, I'll finish your floor with your choice of oil, lacquer or wax for lasting protection and the look you want.",
    icon: Shield,
    features: ["Oil finishes", "Lacquer seal", "Wax treatment", "Staining options"]
  }
];

const SandingPage = () => {
  return (
    <div>
      <Helmet>
        <title>Floor Sanding | Dust-Free Wood Floor Restoration | Expert Carpenter Greater Manchester</title>
        <meta name="description" content="Professional dust-free floor sanding in Greater Manchester. Portable dust extraction for near-zero dust. Hardwood, parquet and floorboard sanding. Call 07300 405045 for a free quote." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/sanding" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is dust-free floor sanding?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I use professional portable dust extraction units that capture dust right at the sander. This means virtually no dust escapes into your home - no dusty furniture, no dust in your lungs, and no massive cleanup afterwards."
                }
              },
              {
                "@type": "Question",
                "name": "How long does floor sanding take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A typical room takes 1-2 days including finishing. Larger areas or floors needing more preparation may take longer. I'll give you a realistic timeframe when I see the job."
                }
              },
              {
                "@type": "Question",
                "name": "Do I need to move furniture?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The room needs to be clear of furniture before I start. You can move it yourself or I can help shift things if needed - just let me know."
                }
              },
              {
                "@type": "Question",
                "name": "What finish options do you offer?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I offer oil, lacquer and wax finishes. Oil gives a natural look and is easy to maintain. Lacquer is harder-wearing for high traffic areas. Wax gives a traditional finish. I can also stain the floor before finishing if you want to change the colour."
                }
              },
              {
                "@type": "Question",
                "name": "How soon can I walk on the floor?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "With lacquer, you can walk on it carefully in socks after 24 hours. Full cure takes about a week. Oil finishes can be walked on sooner but need longer to fully harden. I'll give you specific care instructions for your finish."
                }
              },
              {
                "@type": "Question",
                "name": "Is there really no dust?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "It's near-zero dust, not absolutely zero - but the difference compared to traditional sanding is massive. My dust extraction captures 95%+ of the dust. You won't find dust all over your house."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm md:text-base">Specialist Service</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Dust-Free Floor Sanding
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Bring your wooden floors back to life without the mess. 
                Professional dust extraction means your home stays clean throughout the job.
              </p>
              
              {/* Dust-Free Highlight */}
              <div className="bg-[#C9A227]/20 border border-[#C9A227]/40 rounded-xl p-4 mb-8 max-w-md mx-auto lg:mx-0">
                <div className="flex items-center gap-3">
                  <Wind className="h-8 w-8 text-[#C9A227]" />
                  <div>
                    <p className="text-white font-bold text-lg">Portable Dust Extraction</p>
                    <p className="text-gray-300 text-sm">Near-zero dust - your home stays spotless</p>
                  </div>
                </div>
              </div>
              
              <ul className="space-y-4 mb-8 text-left max-w-md mx-auto lg:mx-0">
                {[
                  "Professional dust extraction units",
                  "Virtually dust-free sanding",
                  "No mess throughout your home",
                  "Beautiful finish guaranteed"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-200 text-base md:text-lg">
                    <div className="w-7 h-7 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-[#C9A227]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-col gap-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-lg h-14 px-8 w-full" data-testid="sanding-get-quote-btn">
                    Get a Free Quote
                    <ChevronRight className="ml-2 h-6 w-6" />
                  </Button>
                </Link>
                <a href="tel:07300405045" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg h-14 w-full">
                    <Phone className="mr-2 h-6 w-6" />
                    07300 405045
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_woodwork-master-2/artifacts/8yqux38x_image%20sanding%20floor.png"
                  alt="Professional floor sanding with dust extraction"
                  className="w-full h-[550px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Dust-Free Matters */}
      <section className="py-14 md:py-20 bg-[#2D5A4A]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Dust-Free Sanding?
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Traditional floor sanding creates clouds of fine dust that gets everywhere. 
              My approach is different.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                title: "No Dusty Home", 
                desc: "Dust extraction captures it at source. No dust settling on furniture, curtains, or in other rooms." 
              },
              { 
                title: "Healthier Environment", 
                desc: "Less airborne dust means better air quality for you, your family and me while I work." 
              },
              { 
                title: "Minimal Cleanup", 
                desc: "When I leave, I leave it clean. No week-long cleanup trying to get dust out of everything." 
              }
            ].map((item, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-14 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">What I Do</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3A3A3A]">
              Floor Sanding Services
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              From basic sanding to full floor restoration - all with dust extraction.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {sandingServices.map((service) => (
              <Card key={service.id} className="group p-5 md:p-8 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white">
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="p-3 md:p-4 bg-[#2D5A4A] rounded-xl group-hover:bg-[#C9A227] transition-colors flex-shrink-0">
                    <service.icon className="h-6 w-6 md:h-7 md:w-7 text-white group-hover:text-black transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-[#3A3A3A] mb-2 md:mb-3">
                      {service.name}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm md:text-base">{service.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, i) => (
                        <span
                          key={i}
                          className="text-xs bg-[#f8f7f5] text-[#2D5A4A] px-3 py-1.5 rounded-full font-medium"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          <div className="mt-10 md:mt-16 text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#2D5A4A] hover:bg-[#234839] h-14 px-10 text-lg font-bold w-full sm:w-auto">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How I Work */}
      <section className="py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">The Process</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A3A3A] mb-4">
              How Floor Sanding Works
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              A careful process for beautiful, long-lasting results.
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              { title: "Preparation", desc: "Room cleared, floor inspected. Any protruding nails punched down, loose boards secured." },
              { title: "Coarse sanding", desc: "First pass with coarse grit removes old finish and levels the floor. Dust extraction running throughout." },
              { title: "Medium sanding", desc: "Second pass with medium grit starts smoothing the surface and removing scratches from coarse sanding." },
              { title: "Fine sanding", desc: "Final sanding pass creates a smooth, even surface ready for finishing." },
              { title: "Finishing", desc: "Your chosen finish applied - oil, lacquer or wax. Multiple coats for lasting protection." }
            ].map((step, i) => (
              <div key={i} className="flex gap-4 items-start bg-white p-5 rounded-xl shadow-sm">
                <div className="w-10 h-10 bg-[#2D5A4A] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-bold text-[#3A3A3A] mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-10 sm:py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">Common Questions</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3A3A3A]">
              Floor Sanding FAQs
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              { 
                q: "What is dust-free floor sanding?", 
                a: "I use professional portable dust extraction units that capture dust right at the sander. This means virtually no dust escapes into your home - no dusty furniture, no dust in your lungs, and no massive cleanup afterwards." 
              },
              { 
                q: "How long does floor sanding take?", 
                a: "A typical room takes 1-2 days including finishing. Larger areas or floors needing more preparation may take longer. I'll give you a realistic timeframe when I see the job." 
              },
              { 
                q: "Do I need to move furniture?", 
                a: "The room needs to be clear of furniture before I start. You can move it yourself or I can help shift things if needed - just let me know." 
              },
              { 
                q: "What finish options do you offer?", 
                a: "I offer oil, lacquer and wax finishes. Oil gives a natural look and is easy to maintain. Lacquer is harder-wearing for high traffic areas. Wax gives a traditional finish. I can also stain the floor before finishing if you want to change the colour." 
              },
              { 
                q: "How soon can I walk on the floor?", 
                a: "With lacquer, you can walk on it carefully in socks after 24 hours. Full cure takes about a week. Oil finishes can be walked on sooner but need longer to fully harden. I'll give you specific care instructions for your finish." 
              },
              { 
                q: "Is there really no dust?", 
                a: "It's near-zero dust, not absolutely zero - but the difference compared to traditional sanding is massive. My dust extraction captures 95%+ of the dust. You won't find dust all over your house." 
              }
            ].map((faq, i) => (
              <div key={i} className="bg-[#f8f7f5] rounded-xl p-4 sm:p-6">
                <h3 className="font-bold text-[#3A3A3A] mb-2 text-base sm:text-lg">{faq.q}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Restore Your Floors?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote for dust-free floor sanding. 
            I'll come and see the job and give you an honest price.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-lg h-14 px-8 w-full sm:w-auto">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:07300405045">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg h-14 w-full sm:w-auto">
                <Phone className="mr-2 h-6 w-6" />
                07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SandingPage;
